import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Send,
  Mail,
  Download,
  Share2,
  ChevronDown,
  Loader2,
  Printer,
  Clock,
} from "lucide-react";

export default function ResumeActionsBar({
  topN,
  setTopN,
  inviting,
  onInviteTopN,
  sendEmails,
  setSendEmails,
  scheduledAt,
  setScheduledAt,
  onExportCSV,
  onPrintTopN,
  printingBundle,
  onOpenAts,
}) {
  const scheduleOn = !!scheduledAt;

  // Build a "YYYY-MM-DDTHH:mm" string from a Date in the user's local tz —
  // matches the format <input type="datetime-local"> expects.
  const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Quick-pick presets that recruiters actually use.
  const presets = [
    {
      key: "tomorrow-8am",
      label: "Tomorrow 8 AM",
      build: () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(8, 0, 0, 0);
        return d;
      },
    },
    {
      key: "mon-9am",
      label: "Mon 9 AM",
      build: () => {
        const d = new Date();
        // 1 = Monday. Always pick the NEXT Monday (skip today if Monday already)
        const day = d.getDay();
        const delta = ((1 - day + 7) % 7) || 7;
        d.setDate(d.getDate() + delta);
        d.setHours(9, 0, 0, 0);
        return d;
      },
    },
    {
      key: "in-1-week",
      label: "In 1 week",
      build: () => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        d.setHours(9, 0, 0, 0);
        return d;
      },
    },
  ];

  // IANA tz name (e.g. "America/Los_Angeles") with a graceful fallback.
  let tzName = "local time";
  try {
    tzName = Intl.DateTimeFormat().resolvedOptions().timeZone || tzName;
  } catch {
    /* ignore */
  }

  return (
    <div
      data-testid="resume-actions-bar"
      className="mt-6 flex flex-wrap items-center gap-3 border border-slate-200 bg-slate-50 p-3"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          Top
        </span>
        <Select value={String(topN)} onValueChange={(v) => setTopN(Number(v))}>
          <SelectTrigger
            data-testid="topn-select"
            className="h-9 w-20 rounded-sm border-slate-300"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[3, 5, 10, 25, 50].map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        data-testid="invite-top-n-btn"
        size="sm"
        onClick={onInviteTopN}
        disabled={inviting}
        className="rounded-sm border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
      >
        {inviting ? (
          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
        ) : scheduleOn ? (
          <Clock className="mr-2 h-3 w-3" />
        ) : (
          <Send className="mr-2 h-3 w-3" />
        )}
        {scheduleOn
          ? `Schedule top ${topN}`
          : `Invite top ${topN} to interview`}
      </Button>

      <label
        data-testid="send-emails-toggle"
        className="flex cursor-pointer items-center gap-2 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-slate-900 hover:bg-emerald-100"
      >
        <input
          type="checkbox"
          checked={sendEmails}
          onChange={(e) => setSendEmails(e.target.checked)}
          className="h-4 w-4 cursor-pointer accent-blue-700"
        />
        <Mail className="h-3 w-3" />
        Email candidates now
      </label>

      <label
        data-testid="schedule-send-wrapper"
        className={`flex flex-wrap items-center gap-2 rounded-sm border px-3 py-1.5 text-xs text-slate-900 ${
          scheduleOn
            ? "border-amber-300 bg-amber-50"
            : "border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        <Clock className="h-3 w-3" />
        <span className="font-medium">Schedule for</span>
        <input
          data-testid="schedule-send-input"
          type="datetime-local"
          value={scheduledAt || ""}
          onChange={(e) => setScheduledAt(e.target.value || "")}
          disabled={!sendEmails}
          className="rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 disabled:opacity-50"
        />
        <span className="text-[10px] text-slate-500" data-testid="schedule-tz-hint">
          {tzName}
        </span>
        <div className="flex items-center gap-1" data-testid="schedule-presets">
          {presets.map((p) => (
            <button
              type="button"
              key={p.key}
              data-testid={`schedule-preset-${p.key}`}
              disabled={!sendEmails}
              onClick={() => setScheduledAt(toLocalInput(p.build()))}
              className="rounded-sm border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-700 hover:border-amber-400 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-50"
              title={`Set to ${p.label} (${tzName})`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {scheduleOn && (
          <button
            type="button"
            data-testid="schedule-send-clear"
            onClick={() => setScheduledAt("")}
            className="text-[11px] font-semibold text-amber-700 underline-offset-2 hover:underline"
            title="Clear schedule — send immediately"
          >
            Clear
          </button>
        )}
      </label>

      <Button
        data-testid="export-csv-btn"
        size="sm"
        onClick={onExportCSV}
        className="rounded-sm border border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200"
      >
        <Download className="mr-2 h-3 w-3" />
        Export CSV
      </Button>

      <Button
        data-testid="print-top-n-btn"
        size="sm"
        onClick={onPrintTopN}
        disabled={printingBundle}
        className="rounded-sm border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"
      >
        {printingBundle ? (
          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
        ) : (
          <Printer className="mr-2 h-3 w-3" />
        )}
        Print top {topN} summaries
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="ats-push-btn"
            size="sm"
            variant="outline"
            className="rounded-sm border-slate-300"
          >
            <Share2 className="mr-2 h-3 w-3" />
            Push to ATS
            <ChevronDown className="ml-2 h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-sm">
          <DropdownMenuItem
            data-testid="ats-greenhouse-item"
            onClick={() => onOpenAts("greenhouse")}
          >
            Greenhouse
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="ats-lever-item"
            onClick={() => onOpenAts("lever")}
          >
            Lever
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
